import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { authConfigured, getIdToken, handleCallback, login, logout, idTokenClaims, REQUIRED_GROUP, LOGIN_BLOCKED_EVENT } from './session'

export function AuthGate({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(false)
  const [unauthorizedEmail, setUnauthorizedEmail] = useState<string | null>(null)
  const [error, setError] = useState('')
  // StrictMode replays effects; callback state must be consumed exactly once.
  const started = useRef(false)
  const configured = authConfigured()

  useEffect(() => {
    if (!configured || started.current) return
    started.current = true
    async function authenticate() {
      if (window.location.pathname === '/auth/callback') {
        const returnTo = await handleCallback()
        window.history.replaceState(null, '', returnTo)
      }
      const token = await getIdToken()
      if (!token) await login()
      else {
        const claims = idTokenClaims(token)
        if (!Array.isArray(claims['cognito:groups']) || !claims['cognito:groups'].includes(REQUIRED_GROUP)) {
          setUnauthorizedEmail(claims.email || '')
        } else setAuthenticated(true)
      }
    }
    void authenticate().catch(() => setError('Sign-in failed. Please sign in again.'))
  }, [configured])

  useEffect(() => {
    const blocked = () => setError('Automatic sign-in paused. Please sign in again.')
    window.addEventListener(LOGIN_BLOCKED_EVENT, blocked)
    return () => window.removeEventListener(LOGIN_BLOCKED_EVENT, blocked)
  }, [])

  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted && !authenticated) {
        setError('Please sign in again.')
      }
    }
    window.addEventListener('pageshow', restored)
    return () => window.removeEventListener('pageshow', restored)
  }, [authenticated])

  if (!configured) return <p>Sign-in is not configured</p>
  if (error) return <div><p>{error}</p><button onClick={() => void login(true).catch(() => setError('Sign-in failed. Please sign in again.'))}>Sign in again</button></div>
  if (unauthorizedEmail !== null) return <div><p>Not authorized for Bookkeeper</p>
    <p>{unauthorizedEmail}</p><button onClick={() => void logout()}>Sign out</button></div>
  if (!authenticated) return <p>Signing in…</p>
  return children
}
